import React, { useState, useEffect } from 'react';
import { Download, Printer, Building2, Search, Users, UserCheck, FileText } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { exportToPDF } from '@/utils/groupReportPDF';

const GroupAttendanceReport = ({ type = 'student' }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fromDate, setFromDate] = useState(new Date().toISOString().split('T')[0]);
  const [toDate, setToDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedCollege, setSelectedCollege] = useState('');
  const [colleges, setColleges] = useState([]);
  const API_URL = process.env.REACT_APP_BACKEND_URL;
  const isStudent = type === 'student';
  const title = isStudent ? 'Student Attendance Report' : 'Staff Attendance Report';
  const endpoint = isStudent ? 'student-attendance' : 'staff-attendance';

  useEffect(() => { fetchColleges(); fetchReport(); }, []);

  const fetchColleges = async () => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API_URL}/api/groups/my-group`, { headers: { Authorization: `Bearer ${token}` } });
      setColleges(res.data.colleges || []);
    } catch (e) { console.error(e); }
  };

  const fetchReport = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let url = `${API_URL}/api/groups/my-group/reports/${endpoint}?from_date=${fromDate}&to_date=${toDate}`;
      if (selectedCollege) url += `&college_id=${selectedCollege}`;
      const res = await axios.get(url, { headers: { Authorization: `Bearer ${token}` } });
      setData(res.data);
    } catch (e) { toast.error('Failed to load report'); }
    finally { setLoading(false); }
  };

  const exportToExcel = () => {
    if (!data?.colleges?.length) return toast.error('No data');
    const ws = XLSX.utils.json_to_sheet(data.colleges.map(c => ({
      'College': c.college_name, 'Total Records': c.total_records, 'Present': c.present,
      'Absent': c.absent, 'Attendance Rate': `${c.attendance_rate}%`
    })));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, title);
    XLSX.writeFile(wb, `${title.replace(/ /g, '_')}_${fromDate}_${toDate}.xlsx`);
  };

  const exportPDF = () => {
    if (!data?.colleges?.length) return toast.error('No data');
    exportToPDF(title,
      ['College', 'Total Records', 'Present', 'Absent', 'Rate'],
      data.colleges.map(c => [c.college_name, c.total_records, c.present, c.absent, `${c.attendance_rate}%`]),
      `${title.replace(/ /g, '_')}_${fromDate}_${toDate}`,
      `Period: ${fromDate} to ${toDate} | Overall Rate: ${data.totals?.attendance_rate||0}%`
    );
  };

  return (
    <div className="p-6 max-w-7xl mx-auto" data-testid={`group-${type}-attendance-report`}>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
          <p className="text-sm text-gray-500">Attendance summary across all colleges</p>
        </div>
        <div className="flex gap-2">
          <button onClick={exportToExcel} className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 text-sm"><Download className="h-4 w-4" /> Excel</button>
          <button onClick={exportPDF} className="flex items-center gap-2 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"><FileText className="h-4 w-4" /> PDF</button>
          <button onClick={() => window.print()} className="flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 text-sm"><Printer className="h-4 w-4" /> Print</button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow p-4 mb-6 flex flex-wrap gap-4 items-end">
        <div><label className="block text-xs font-medium text-gray-600 mb-1">From</label><input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">To</label><input type="date" value={toDate} onChange={e => setToDate(e.target.value)} className="border rounded-lg px-3 py-2 text-sm" /></div>
        <div><label className="block text-xs font-medium text-gray-600 mb-1">College</label>
          <select value={selectedCollege} onChange={e => setSelectedCollege(e.target.value)} className="border rounded-lg px-3 py-2 text-sm">
            <option value="">All Colleges</option>{colleges.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
          </select>
        </div>
        <button onClick={fetchReport} className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm flex items-center gap-2"><Search className="h-4 w-4" /> Search</button>
      </div>

      {loading ? <div className="text-center py-12 text-gray-500">Loading...</div> : data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
            <div className="bg-white rounded-xl shadow p-5"><p className="text-xs text-gray-500">Total Records</p><p className="text-2xl font-bold">{data.totals?.total_records || 0}</p></div>
            <div className="bg-white rounded-xl shadow p-5"><p className="text-xs text-gray-500">Present</p><p className="text-2xl font-bold text-green-600">{data.totals?.total_present || 0}</p></div>
            <div className="bg-white rounded-xl shadow p-5"><p className="text-xs text-gray-500">Absent</p><p className="text-2xl font-bold text-red-600">{data.totals?.total_absent || 0}</p></div>
            <div className="bg-white rounded-xl shadow p-5"><p className="text-xs text-gray-500">Attendance Rate</p><p className="text-2xl font-bold text-blue-600">{data.totals?.attendance_rate || 0}%</p></div>
          </div>

          <div className="bg-white rounded-xl shadow">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="text-left p-4">College</th>
                    <th className="text-center p-4">Total Records</th>
                    <th className="text-center p-4">Present</th>
                    <th className="text-center p-4">Absent</th>
                    <th className="text-center p-4">Rate</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {data.colleges?.map(c => (
                    <tr key={c.college_id} className="hover:bg-gray-50">
                      <td className="p-4 font-medium">{c.college_name}</td>
                      <td className="p-4 text-center">{c.total_records}</td>
                      <td className="p-4 text-center text-green-600 font-medium">{c.present}</td>
                      <td className="p-4 text-center text-red-600 font-medium">{c.absent}</td>
                      <td className="p-4 text-center">
                        <span className={`px-2 py-1 rounded text-xs font-bold ${c.attendance_rate >= 75 ? 'bg-green-100 text-green-700' : c.attendance_rate >= 50 ? 'bg-yellow-100 text-yellow-700' : 'bg-red-100 text-red-700'}`}>
                          {c.attendance_rate}%
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default GroupAttendanceReport;
